import React, { useMemo, useState } from 'react';
import InvestmentSectionTabs from './InvestmentSectionTabs';
import { INVESTMENT_FORM_TABS } from './investmentFormUtils';

const formatPercent = (value) => {
    if (value == null) return '—';
    return `${Number(value).toFixed(4).replace(/\.?0+$/, '')}%`;
};

const Field = ({ label, value }) => (
    <div>
        <span className="text-gray-500">{label}</span>
        <p className="font-medium text-gray-900 mt-0.5">{value ?? '—'}</p>
    </div>
);

const InvestmentTabbedDetails = ({ investment, formatMoney, formatDateTime }) => {
    const [activeTab, setActiveTab] = useState(INVESTMENT_FORM_TABS[0].id);

    const content = useMemo(() => {
        switch (activeTab) {
            case 'overview':
                return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 text-sm">
                        <Field label="Amount" value={formatMoney(investment.investmentAmount, investment.currency)} />
                        <Field label="Currency" value={investment.currency || '—'} />
                        <Field label="Investment date" value={investment.investmentDate ? formatDateTime(investment.investmentDate) : '—'} />
                        <Field label="Stage" value={investment.investmentStage || '—'} />
                        <Field label="Type" value={investment.investmentType || '—'} />
                        <Field label="Holding %" value={formatPercent(investment.equityPercentage)} />
                        <Field label="Access level" value={`${investment.accessLevel || '—'}${investment.autoGrantAccess === false ? ' (auto-grant off)' : ''}`} />
                        <Field label="Board seat" value={investment.boardSeat ? 'Yes' : 'No'} />
                        <Field label="Lead investor" value={investment.leadInvestor ? 'Yes' : 'No'} />
                    </div>
                );
            case 'share-details':
                return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 text-sm">
                        <Field label="Entry share price" value={investment.entrySharePrice != null ? formatMoney(investment.entrySharePrice, investment.currency) : '—'} />
                        <Field label="Entry share equity" value={investment.entryShareEquity ?? '—'} />
                        <Field label="No. of CCPS" value={investment.numberOfShares ?? '—'} />
                        <Field label="Partly paid amount" value={investment.partlyPaidAmount != null ? formatMoney(investment.partlyPaidAmount, investment.currency) : '—'} />
                        <Field label="Nett subscription amount" value={investment.nettSubscriptionAmount != null ? formatMoney(investment.nettSubscriptionAmount, investment.currency) : '—'} />
                    </div>
                );
            case 'tranche-1':
                return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 text-sm">
                        <Field label="Tranche-1 subscription amount" value={investment.tranche1SubscriptionAmount != null ? formatMoney(investment.tranche1SubscriptionAmount, investment.currency) : '—'} />
                        <Field label="Tranche-1 No. of CCPS" value={investment.tranche1NoCcps ?? '—'} />
                        <Field label="Tranche-1 holding %" value={formatPercent(investment.tranche1HoldingPercentage)} />
                    </div>
                );
            case 'tranche-2':
                return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 text-sm">
                        <Field label="Tranche-2 subscription amount" value={investment.tranche2SubscriptionAmount != null ? formatMoney(investment.tranche2SubscriptionAmount, investment.currency) : '—'} />
                        <Field label="Tranche-2 No. of CCPS" value={investment.tranche2NoCcps ?? '—'} />
                        <Field label="Tranche-2 holding %" value={formatPercent(investment.tranche2HoldingPercentage)} />
                    </div>
                );
            case 'tranche-3':
                return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 text-sm">
                        <Field label="Tranche-3 subscription amount" value={investment.postCompletionSubscriptionAmount != null ? formatMoney(investment.postCompletionSubscriptionAmount, investment.currency) : '—'} />
                        <Field label="Tranche-3 No. of CCPS" value={investment.postCompletionNoCcps ?? '—'} />
                        <Field label="Tranche-3 holding %" value={formatPercent(investment.postCompletionHoldingPercentage)} />
                    </div>
                );
            case 'post-exit':
                return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 text-sm">
                        <Field label="Exit date" value={investment.exitDate ? formatDateTime(investment.exitDate) : '—'} />
                        <Field label="Exit amount" value={investment.exitAmount != null ? formatMoney(investment.exitAmount, investment.currency) : '—'} />
                        <Field label="Exit valuation" value={investment.exitValuation != null ? formatMoney(investment.exitValuation, investment.currency) : '—'} />
                        <div className="sm:col-span-2 lg:col-span-3">
                            <span className="text-gray-500">Notes</span>
                            <p className="font-medium text-gray-900 whitespace-pre-wrap mt-0.5">{investment.notes?.trim() ? investment.notes : '—'}</p>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    }, [activeTab, formatDateTime, formatMoney, investment]);

    return (
        <div className="space-y-4">
            <InvestmentSectionTabs tabs={INVESTMENT_FORM_TABS} activeTab={activeTab} onChange={setActiveTab} />
            <div className="rounded-xl border border-gray-200 bg-white p-4">
                {content}
            </div>
        </div>
    );
};

export default InvestmentTabbedDetails;
